import { lazy, Suspense } from "react";

import { QueryClient, QueryClientProvider } from "react-query";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";

import "react-toastify/dist/ReactToastify.css";
import "./styles/toastify-overrides.css";
import GlobalPlayerOverlay from "./components/GlobalPlayerOverlay";
import HeartbeatManager from "./components/HeartbeatManager";
import ReminderManager from "./components/ReminderManager";
import SportsAlertBanner from "./components/SportsAlertBanner";
import UserOnlyRoute from "./components/UserOnlyRoute";
import { GradientProvider } from "./contexts/GradientContext";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import NotFound from "./pages/NotFound";
import WebPlayerAvatars from "./pages/WebPlayerAvatars";
import WebPlayerCastDetails from "./pages/WebPlayerCastDetails";
import WebPlayerDiscover from "./pages/WebPlayerDiscover";
import WebPlayerEditProfile from "./pages/WebPlayerEditProfile";
import WebPlayerFavoritesGrid from "./pages/WebPlayerFavoritesGrid";
import WebPlayerFindChannel from "./pages/WebPlayerFindChannel";
import WebPlayerHome from "./pages/WebPlayerHome";
import WebPlayerLeagueEvents from "./pages/WebPlayerLeagueEvents";
import WebPlayerLiveTV from "./pages/WebPlayerLiveTV";
import WebPlayerLogin from "./pages/WebPlayerLogin";
import WebPlayerMovieDetails from "./pages/WebPlayerMovieDetails";
import WebPlayerMovies from "./pages/WebPlayerMovies";
import WebPlayerMultiView from "./pages/WebPlayerMultiView";
import WebPlayerPlayerDetail from "./pages/WebPlayerPlayerDetail";
import WebPlayerProfiles from "./pages/WebPlayerProfiles";
import WebPlayerSearch from "./pages/WebPlayerSearch";
import WebPlayerSeries from "./pages/WebPlayerSeries";
import WebPlayerSeriesDetails from "./pages/WebPlayerSeriesDetails";
import WebPlayerSettings from "./pages/WebPlayerSettings";
import WebPlayerSetup from "./pages/WebPlayerSetup";
import WebPlayerSpeedTest from "./pages/WebPlayerSpeedTest";
import WebPlayerSportHub from "./pages/WebPlayerSportHub";
import WebPlayerSportHubAll from "./pages/WebPlayerSportHubAll";
import WebPlayerSportsTeams from "./pages/WebPlayerSportsTeams";
import WebPlayerSports from "./pages/WebPlayerSportsTest";
import WebPlayerSportsEventDetail from "./pages/WebPlayerSportsTestEventDetail";
import WebPlayerSwitchPlaylist from "./pages/WebPlayerSwitchPlaylist";
import WebPlayerTeamDetail from "./pages/WebPlayerTeamDetail";
import WebPlayerWatchedTv from "./pages/WebPlayerWatchedTv";
import WebPlayerWatchlist from "./pages/WebPlayerWatchlist";

// Lazy load sports sub-pages for better performance
const WebPlayerSportsLeagues = lazy(
  () => import("./pages/WebPlayerSportsLeagues")
);
const WebPlayerLeagueDetail = lazy(
  () => import("./pages/WebPlayerLeagueDetail")
);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const LandscapeNotice = () => (
  <div
    className="fixed inset-0 z-[100000] hidden flex-col items-center justify-center gap-6 px-10 text-center portrait:flex lg:!hidden"
    style={{
      background:
        "linear-gradient(180deg, #1c1c2a 0%, #0e0e18 46%, #030305 100%)",
    }}
  >
    <i
      aria-hidden="true"
      className="fa-regular fa-mobile-rotate-reverse text-6xl text-white"
    />
    <div>
      <p className="text-2xl font-semibold text-white">Flip your phone</p>
      <p className="mt-2 text-base text-white/70">
        Rotate to landscape to use Lunova.
      </p>
    </div>
  </div>
);

// Component to manage browser title and toast
function AppContent() {
  const { isDarkMode } = useTheme();

  return (
    <Router>
      <LandscapeNotice />
      <HeartbeatManager />
      <ReminderManager />
      <GlobalPlayerOverlay />
      <SportsAlertBanner enabled pollIntervalMs={15000} />
      <ToastContainer
        closeOnClick
        draggable
        pauseOnFocusLoss
        pauseOnHover
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop={false}
        position="top-right"
        rtl={false}
        style={{
          zIndex: 999999,
        }}
        theme={isDarkMode ? "dark" : "light"}
      />
      <Routes>
        <Route index element={<Navigate replace to="/web-player/home" />} />
        <Route
          element={<Navigate replace to="/web-player/home" />}
          path="/web-player"
        />
        <Route element={<WebPlayerLogin />} path="/web-player/login" />
        <Route
          element={<WebPlayerSwitchPlaylist />}
          path="/web-player/switch-playlist"
        />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerSetup />
            </UserOnlyRoute>
          }
          path="/web-player/setup"
        />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerProfiles />
            </UserOnlyRoute>
          }
          path="/web-player/profiles"
        />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerEditProfile />
            </UserOnlyRoute>
          }
          path="/web-player/edit-profile/:profileId"
        />
        <Route element={<WebPlayerHome />} path="/web-player/home" />
        <Route element={<WebPlayerDiscover />} path="/web-player/discover" />
        <Route element={<WebPlayerSearch />} path="/web-player/search" />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerLiveTV />
            </UserOnlyRoute>
          }
          path="/web-player/live-tv"
        />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerMultiView />
            </UserOnlyRoute>
          }
          path="/web-player/multiview"
        />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerWatchlist />
            </UserOnlyRoute>
          }
          path="/web-player/watchlist"
        />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerWatchedTv />
            </UserOnlyRoute>
          }
          path="/web-player/watched-tv"
        />
        <Route element={<WebPlayerSeries />} path="/web-player/series" />
        <Route element={<WebPlayerMovies />} path="/web-player/movies" />
        <Route
          element={<WebPlayerMovies />}
          path="/web-player/movies/watched"
        />
        <Route
          element={<WebPlayerMovies />}
          path="/web-player/movies/collections"
        />
        <Route element={<WebPlayerSettings />} path="/web-player/settings" />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerSpeedTest />
            </UserOnlyRoute>
          }
          path="/web-player/speed-test"
        />
        <Route element={<WebPlayerSports />} path="/web-player/sports" />
        <Route
          element={<WebPlayerFindChannel />}
          path="/web-player/sports/find-channel"
        />
        <Route
          element={<WebPlayerSportsEventDetail />}
          path="/web-player/sports/event/:eventId"
        />
        <Route
          element={<WebPlayerSportsTeams />}
          path="/web-player/sports/teams"
        />
        <Route
          element={
            <Suspense
              fallback={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-white">
                    <i className="fas fa-spinner fa-spin text-4xl" />
                  </div>
                </div>
              }
            >
              <WebPlayerSportsLeagues />
            </Suspense>
          }
          path="/web-player/sports/leagues"
        />
        <Route
          element={
            <Suspense
              fallback={
                <div className="min-h-screen flex items-center justify-center">
                  <div className="text-white">
                    <i className="fas fa-spinner fa-spin text-4xl" />
                  </div>
                </div>
              }
            >
              <WebPlayerLeagueDetail />
            </Suspense>
          }
          path="/web-player/sports/league/:leagueId"
        />
        <Route
          element={<WebPlayerPlayerDetail />}
          path="/web-player/sports/player/:playerId"
        />
        <Route
          element={<WebPlayerTeamDetail />}
          path="/web-player/sports/team/:teamName"
        />
        <Route
          element={<WebPlayerLeagueEvents />}
          path="/web-player/sports/league-events/:leagueName"
        />
        <Route
          element={<WebPlayerSportHubAll />}
          path="/web-player/sports/hub/:sportName/all"
        />
        <Route
          element={<WebPlayerSportHub />}
          path="/web-player/sports/hub/:sportName"
        />
        <Route
          element={<WebPlayerMovieDetails />}
          path="/web-player/movie/:mediaId"
        />
        <Route
          element={<WebPlayerSeriesDetails />}
          path="/web-player/series/:seriesId"
        />
        <Route
          element={<WebPlayerCastDetails />}
          path="/web-player/cast/:castId"
        />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerAvatars />
            </UserOnlyRoute>
          }
          path="/web-player/avatars"
        />
        <Route
          element={
            <UserOnlyRoute>
              <WebPlayerFavoritesGrid />
            </UserOnlyRoute>
          }
          path="/web-player/favorites/:type"
        />
        <Route element={<NotFound />} path="*" />
      </Routes>
    </Router>
  );
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <GradientProvider>
        <ThemeProvider>
          <AppContent />
        </ThemeProvider>
      </GradientProvider>
    </QueryClientProvider>
  );
}

export default App;
